import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { PanelsInterfaceComponent } from '../panels-interface.component';
import { Property } from '../../../../../projects/projects.types';
import { Account } from '@core/models';
import { PanelDef } from '@shared/components/panels/panels.types';
import { TsBadgeModule, TsButtonModule, TSIconModule, TsSnackbarService } from 'tsui';
import { TsEmptyStateComponent } from 'tsui/lib/ts-empty-state/ts-empty-state.component';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { TsRxjsOperators } from '@shared/Utils/custom-rxjs-operators.utils';
import { AssetManagementMessages } from '../../../../../asset-management/utils/asset-management-messages.utils';
import { Store } from '@ngxs/store';
import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import {
  FinancialReportPanelBuilderDialogComponent,
  FinancialReportPanelBuilderDialogData,
} from '../../../../../asset-management/components/financial-report-panel-builder-dialog/financial-report-panel-builder-dialog.component';
import { FinancialStatementGridComponent } from '../../../../../asset-management/components/financial-statement-grid/financial-statement-grid.component';
import { AssetManagementService } from '../../../../../asset-management/services/asset-management.service';
import {
  BalanceSheetEntry,
  ChartVisualizationConfig,
  FinancialStatementEntry,
} from '../../../../../asset-management/models';
import { TsPanelComponent } from 'tsui/lib/ts-panel/ts-panel.component';
import { ReportPeriodUtils } from '../../../../../asset-management/utils/report-period.utils';
import { Router } from '@angular/router';
import { RelatedToType } from '@shared/enums';

@Component({
  selector: 'financial-report-panel',
  standalone: true,
  imports: [
    TSIconModule,
    TsEmptyStateComponent,
    TsButtonModule,
    NgClass,
    MatButtonModule,
    FinancialStatementGridComponent,
    AsyncPipe,
    NgStyle,
    TsPanelComponent,
    TsBadgeModule,
  ],
  templateUrl: './financial-report-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinancialReportPanelComponent implements PanelsInterfaceComponent, OnInit {
  @Input() property: Property;
  @Input() account: Account;
  @Input() panelDef: PanelDef<FinancialReportPanelBuilderDialogData>;
  @Input() data: any;
  @Input() canEditPanelDef: boolean = true;
  @Output() onUpdate: EventEmitter<string> = new EventEmitter();
  @Output() onEditPanelDef: EventEmitter<any> = new EventEmitter();

  readonly createNewFinancialReportConfig: { label: string; actionFn: () => void } = {
    label: 'Create New Financial Report',
    actionFn: () => {
      this.openPanelCreationDialog();
    },
  };
  readonly router = inject(Router);
  reportPeriodLabels = ReportPeriodUtils.reportPeriodLabels;
  financialReportDataSource$: Observable<FinancialStatementEntry[] | BalanceSheetEntry[]>;

  readonly dialog = inject(Dialog);
  readonly store = inject(Store);
  readonly snackbar = inject(TsSnackbarService);
  readonly assetManagementService = inject(AssetManagementService);
  readonly loading = new BehaviorSubject<boolean>(true);

  get hasPanelData(): boolean {
    // check if the object isn't empty
    return this.panelDef?.meta && Object.keys(this.panelDef.meta).length > 0;
  }

  get financialReportVisualizationConfig(): FinancialReportPanelBuilderDialogData {
    return this.panelDef?.meta;
  }

  // hack: Overview and tabs have diff grid implementations, since some panel layouts behave differently
  // this is needed.
  get isOverviewTab() {
    // sometimes overview tab is just 'details' on the url 🤷🏻
    return this.router.url.includes('overview') || !this.router.url.includes('tabs');
  }

  ngOnInit(): void {
    if (this.property?.id && this.hasPanelData) {
      this.financialReportDataSource$ = this.assetManagementService
        .getFinancialStatement(
          { related_to_id: this.property.id, related_to_type: RelatedToType.Property },
          this.financialReportVisualizationConfig.type,
          this.financialReportVisualizationConfig.reportPeriod,
        )
        .pipe(tap(() => this.loading.next(false)));
    }
  }

  openPanelCreationDialog(financialReportVisualizationConfig?: FinancialReportPanelBuilderDialogData): void {
    const dialogRef: DialogRef = this.dialog.open(FinancialReportPanelBuilderDialogComponent, {
      ...TsDialogComponent.getDefaultDialogConfig(),
      maxWidth: '1200px',
      data: <FinancialReportPanelBuilderDialogData>{
        propertyId: this.property.id,
        ...(financialReportVisualizationConfig && { ...financialReportVisualizationConfig }),
      },
    });

    // TODO abstract this to a service
    dialogRef.closed
      .pipe(
        filter((result) => !!result),
        take(1),
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbar,
          AssetManagementMessages.Success.CREATE('Panel'),
          AssetManagementMessages.Error.CREATE('Panel'),
        ),
      )
      .subscribe((result: ChartVisualizationConfig) => {
        // TODO when abstracting, add this to a tap operator
        this.onEditPanelDef.emit({
          ...this.panelDef,
          meta: { ...result },
        });
      });
  }
}
